import {
    AFORO_AREAS,
    AFORO_EVACUACION,
    AFORO_ZONES,
    AforoAreaId,
    AforoPuntoRef,
    AforoRect,
    AforoZone,
    expandPlazas,
} from "./aforoConfig";

// Simulación de evacuación de una planta, a partir de la gente que hay en
// cada zona en la foto elegida. Persona a persona:
//  - cada persona empieza en un punto de su zona (repartidos por sus
//    rectángulos, o en su plaza en el parking), de modo que la gente "sale
//    de todos lados" y el mapa de calor y los puntos cuentan lo mismo;
//  - tras unos segundos de reacción, cada zona se vacía por su puerta a un
//    caudal fijo (una persona cada 1/caudal segundos, con algo de dispersión);
//  - cada persona sigue su RUTA hasta la salida más cercana: en recta hasta
//    la espina que lleva a esa salida (solo por sus tramos de entrada, nunca
//    por el último) y por ella hasta la salida; o en recta si la salida no
//    tiene espina;
//  - en cada salida hay otro caudal: si llega más gente de la que cabe, se
//    forma cola (la gente espera en fila sobre la ruta).
// De ahí salen la línea de tiempo por zonas (chips), la posición de cada
// persona en cada instante (puntos en movimiento y mapa de calor) y el
// resumen: duración, objetivo, última zona en vaciarse y cola máxima.

const DT = 0.5; // segundos simulados por foto de la línea de tiempo
const MAX_SECONDS = 1800;

export interface Punto {
    x: number;
    z: number;
}

export interface EvacuationRoute {
    zoneId: string;
    exitIndex: number;
    points: Punto[];
    /** Longitud total (unidades del modelo). */
    length: number;
    /** Índice del punto donde la ruta se incorpora a la espina (0 si va en recta). */
    joinIndex: number;
}

export interface EvacuationPerson {
    zoneId: string;
    start: Punto;
    route: EvacuationRoute;
    /** Segundo en el que sale de su zona, llega a la salida y cruza la salida. */
    tDep: number;
    tArr: number;
    tExit: number;
    speed: number;
}

export interface EvacuationPosition {
    person: EvacuationPerson;
    /** null cuando ya ha salido de la planta. */
    pos: Punto | null;
}

export interface EvacuationFrame {
    t: number;
    counts: { [zoneId: string]: number };
    remaining: number;
}

export interface EvacuationZoneResult {
    zone: AforoZone;
    personas: number;
    /** Longitud de la ruta desde el centro de la zona hasta su salida. */
    distancia: number;
    /** Segundo en el que salió la última persona de la zona. */
    vaciadaEn: number;
}

export interface EvacuationInput {
    zone: AforoZone;
    personas: number;
    /** Puntos de partida (p. ej. las plazas ocupadas); si faltan, al azar en la zona. */
    starts?: Punto[];
}

export interface EvacuationResult {
    areaId: AforoAreaId;
    personas: number;
    /** Segundos hasta que cruza la salida la última persona. */
    duracion: number;
    objetivo: number;
    cumple: boolean;
    /** Zona que más tarda en vaciarse (excluida la de paso). */
    ultimaZona: EvacuationZoneResult | null;
    /** Personas esperando en las salidas en el peor momento. */
    colaMaxima: number;
    zonas: EvacuationZoneResult[];
    transitoId?: string;
    frames: EvacuationFrame[];
    gente: EvacuationPerson[];
}

export const formatSeconds = (s: number): string => {
    const total = Math.max(0, Math.round(s));
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(
        2,
        "0"
    )}`;
};

/** "2 minutos y 40 segundos", "45 segundos". */
export const describeSeconds = (s: number): string => {
    const total = Math.max(0, Math.round(s));
    const m = Math.floor(total / 60);
    const sec = total % 60;
    if (m === 0) return `${sec} segundos`;
    const minutos = `${m} ${m === 1 ? "minuto" : "minutos"}`;
    return sec === 0 ? minutos : `${minutos} y ${sec} segundos`;
};

// ---- Geometría ----

const dist = (a: Punto, b: Punto): number => Math.hypot(a.x - b.x, a.z - b.z);

const polylineLength = (points: Punto[]): number =>
    points.reduce((acc, p, i) => (i === 0 ? 0 : acc + dist(points[i - 1], p)), 0);

/** Punto de una polilínea a una distancia desde su inicio (acotada). */
export const pointAlong = (points: Punto[], distance: number): Punto => {
    if (points.length === 0) return { x: 0, z: 0 };
    let left = Math.max(0, distance);
    for (let i = 1; i < points.length; i += 1) {
        const a = points[i - 1];
        const b = points[i];
        const d = dist(a, b);
        if (left <= d && d > 0) {
            const t = left / d;
            return { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
        }
        left -= d;
    }
    return points[points.length - 1];
};

const zonePoint = (zoneId: string): Punto | null => {
    const zone = AFORO_ZONES.find((z) => z.id === zoneId);
    return zone?.position ? { x: zone.position.x, z: zone.position.z } : null;
};

/** Un punto de la configuración → coordenadas (null si su zona aún no tiene posición). */
const resolvePoint = (ref: AforoPuntoRef): Punto | null => {
    if (!("zona" in ref)) return ref;
    const base = zonePoint(ref.zona);
    if (!base) return null;
    const hacia = ref.haciaZona ? zonePoint(ref.haciaZona) : null;
    const f = ref.fraccion ?? 0;
    return hacia
        ? { x: base.x + (hacia.x - base.x) * f, z: base.z + (hacia.z - base.z) * f }
        : base;
};

/** Salidas de una planta. */
export const exitPoints = (areaId: AforoAreaId): Punto[] => {
    const area = AFORO_AREAS.find((a) => a.id === areaId);
    return (area?.salidas ?? []).map(resolvePoint).filter((p): p is Punto => p !== null);
};

/** Espinas de la planta (cada una acaba en una salida), ya en coordenadas. */
export const spinesOf = (areaId: AforoAreaId): Punto[][] => {
    const area = AFORO_AREAS.find((a) => a.id === areaId);
    return (area?.espinas ?? [])
        .map((espina) => espina.map(resolvePoint).filter((p): p is Punto => p !== null))
        .filter((espina) => espina.length >= 2);
};

/** Cota del suelo de la planta (la de sus zonas). */
export const floorY = (areaId: AforoAreaId): number => {
    const ys = AFORO_ZONES.filter((z) => z.area === areaId && z.position).map((z) => z.position!.y);
    return ys.length > 0 ? Math.min(...ys) : 0;
};

const near = (a: Punto, b: Punto): boolean => dist(a, b) < 0.5;

/**
 * Ruta desde un punto hasta una salida: por la espina que lleva a esa salida
 * (en recta hasta su punto más cercano, entrando solo por los tramos
 * permitidos, y luego por ella), o en recta si no hay espina.
 */
const routeTo = (
    start: Punto,
    exit: Punto,
    exitIndex: number,
    spines: Punto[][],
    zoneId: string
): EvacuationRoute => {
    let points: Punto[] = [start, exit];
    let joinIndex = 0;
    const espina = spines.find((s) => near(s[s.length - 1], exit));
    if (espina) {
        // Tramos por los que se puede entrar: todos menos el último (que ya
        // es la salida), salvo que la espina sea un único tramo.
        const lastAllowed = espina.length > 2 ? espina.length - 2 : espina.length - 1;
        let best = { d: Infinity, seg: 1, q: start };
        for (let i = 1; i <= lastAllowed; i += 1) {
            const a = espina[i - 1];
            const b = espina[i];
            const abx = b.x - a.x;
            const abz = b.z - a.z;
            const len2 = abx * abx + abz * abz;
            const t =
                len2 > 0
                    ? Math.max(
                          0,
                          Math.min(1, ((start.x - a.x) * abx + (start.z - a.z) * abz) / len2)
                      )
                    : 0;
            const q = { x: a.x + abx * t, z: a.z + abz * t };
            const d = dist(start, q);
            if (d < best.d) best = { d, seg: i, q };
        }
        points = [start, best.q, ...espina.slice(best.seg)];
        joinIndex = 1;
    }
    // Sin puntos repetidos (tramos de longitud cero).
    const clean: Punto[] = [];
    let cleanJoin = joinIndex;
    points.forEach((p, i) => {
        if (i === 0 || dist(p, points[i - 1]) > 1) {
            clean.push(p);
        } else if (i <= joinIndex) {
            cleanJoin = Math.max(0, cleanJoin - 1);
        }
    });
    return {
        zoneId,
        exitIndex,
        points: clean,
        length: polylineLength(clean),
        joinIndex: cleanJoin,
    };
};

/** La ruta más corta desde un punto a cualquiera de las salidas. */
const bestRoute = (
    start: Punto,
    exits: Punto[],
    spines: Punto[][],
    zoneId: string
): EvacuationRoute | null => {
    let best: EvacuationRoute | null = null;
    exits.forEach((exit, i) => {
        const r = routeTo(start, exit, i, spines, zoneId);
        if (!best || r.length < best.length) best = r;
    });
    return best;
};

/**
 * Rutas de evacuación para dibujar: desde cada zona hasta cada salida (en
 * plantas con varias salidas la gente de una misma zona puede ir a
 * cualquiera). En las zonas con plazas (parking) sale una ruta de cada
 * plaza, no del centro de la fila.
 */
export const evacuationRoutes = (areaId: AforoAreaId): EvacuationRoute[] => {
    const exits = exitPoints(areaId);
    if (exits.length === 0) return [];
    const spines = spinesOf(areaId);
    const routes: EvacuationRoute[] = [];
    AFORO_ZONES.filter(
        (z) => z.area === areaId && !z.hidden && !z.soloEtiqueta && z.position
    ).forEach((z) => {
        const plazas = expandPlazas(z);
        const starts: Punto[] =
            plazas.length > 0 ? plazas : [{ x: z.position!.x, z: z.position!.z }];
        starts.forEach((start) => {
            exits.forEach((exit, i) => routes.push(routeTo(start, exit, i, spines, z.id)));
        });
    });
    return routes;
};

// ---- Simulación ----

// Generador sencillo y determinista (la misma foto da la misma evacuación).
// Operaciones bit a bit intencionadas: son la base del generador.
/* eslint-disable no-bitwise */
const lcg = (seed: number) => {
    let s = seed >>> 0 || 1;
    return () => {
        s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
        return s / 4294967296;
    };
};
/* eslint-enable no-bitwise */

/** Punto al azar dentro de los rectángulos de una zona (con margen a los bordes). */
const randomInRects = (rects: AforoRect[], rand: () => number): Punto => {
    const total = rects.reduce((acc, r) => acc + r.w * r.d, 0);
    let pick = rand() * total;
    let rect = rects[rects.length - 1];
    for (let i = 0; i < rects.length; i += 1) {
        const a = rects[i].w * rects[i].d;
        if (pick <= a) {
            rect = rects[i];
            break;
        }
        pick -= a;
    }
    const mx = Math.min(rect.w * 0.4, 0.6);
    const mz = Math.min(rect.d * 0.4, 0.6);
    return {
        x: rect.x - rect.w / 2 + mx + rand() * Math.max(0, rect.w - 2 * mx),
        z: rect.z - rect.d / 2 + mz + rand() * Math.max(0, rect.d - 2 * mz),
    };
};

/**
 * Posición de una persona en el instante t: en su sitio antes de salir, por
 * su ruta mientras camina, en la fila de su salida mientras espera (según
 * `queueIndex`, 0 = la primera) y null cuando ya ha cruzado la salida.
 */
export const personPosition = (
    p: EvacuationPerson,
    t: number,
    queueIndex: number
): Punto | null => {
    if (t >= p.tExit) return null;
    if (t < p.tDep) return p.start;
    if (t < p.tArr) {
        return pointAlong(p.route.points, (t - p.tDep) * p.speed);
    }
    const back = (queueIndex + 1) * AFORO_EVACUACION.separacionCola;
    return pointAlong(p.route.points, Math.max(0, p.route.length - back));
};

/** Dónde está cada persona en el instante t (con su puesto en la cola de su salida). */
export const evacuationPositions = (result: EvacuationResult, t: number): EvacuationPosition[] => {
    const waitingByExit: { [exitIndex: number]: EvacuationPerson[] } = {};
    result.gente.forEach((p) => {
        if (t >= p.tArr && t < p.tExit) {
            if (!waitingByExit[p.route.exitIndex]) waitingByExit[p.route.exitIndex] = [];
            waitingByExit[p.route.exitIndex].push(p);
        }
    });
    const queueIndex = new Map<EvacuationPerson, number>();
    Object.values(waitingByExit).forEach((list) => {
        list.sort((a, b) => a.tArr - b.tArr).forEach((p, i) => queueIndex.set(p, i));
    });
    return result.gente.map((person) => ({
        person,
        pos: personPosition(person, t, queueIndex.get(person) ?? 0),
    }));
};

export const simulateEvacuation = (
    areaId: AforoAreaId,
    initial: EvacuationInput[]
): EvacuationResult => {
    const cfg = AFORO_EVACUACION;
    const area = AFORO_AREAS.find((a) => a.id === areaId);
    const transitoId = area?.transito;
    const exits = exitPoints(areaId);
    const spines = spinesOf(areaId);

    const zonas = initial.filter((s) => s.personas > 0 && !s.zone.hidden && !s.zone.soloEtiqueta);
    const total = zonas.reduce((acc, z) => acc + z.personas, 0);
    const rand = lcg(total * 7919 + areaId.length);

    // Una persona por cada una de la foto: su punto de partida, su salida
    // escalonada por la puerta de su zona, su ruta y su paso.
    const gente: EvacuationPerson[] = [];
    const resultados: EvacuationZoneResult[] = [];
    zonas.forEach((z) => {
        const center: Punto = z.zone.position
            ? { x: z.zone.position.x, z: z.zone.position.z }
            : { x: 0, z: 0 };
        const offset = rand() * cfg.dispersionSalida;
        let ultimaSalida = 0;
        for (let k = 0; k < z.personas; k += 1) {
            let start = center;
            if (z.starts && k < z.starts.length) start = z.starts[k];
            else if (z.zone.rects && z.zone.rects.length > 0) {
                start = randomInRects(z.zone.rects, rand);
            }
            const route = bestRoute(start, exits, spines, z.zone.id) ?? {
                zoneId: z.zone.id,
                exitIndex: 0,
                points: [start],
                length: 0,
                joinIndex: 0,
            };
            const tDep = cfg.retardo + offset + k / cfg.caudalZona + rand() * 0.6;
            const speed = cfg.velocidad * (0.85 + rand() * 0.3);
            const tArr = tDep + route.length / speed;
            gente.push({ zoneId: z.zone.id, start, route, tDep, tArr, tExit: tArr, speed });
            ultimaSalida = Math.max(ultimaSalida, tDep);
        }
        const centerRoute = bestRoute(center, exits, spines, z.zone.id);
        resultados.push({
            zone: z.zone,
            personas: z.personas,
            distancia: centerRoute?.length ?? 0,
            vaciadaEn: ultimaSalida,
        });
    });

    // Cola en cada salida: por orden de llegada, una persona cada 1/caudal.
    gente.sort((a, b) => a.tArr - b.tArr);
    const libreDesde: { [exitIndex: number]: number } = {};
    for (let i = 0; i < gente.length; i += 1) {
        const persona = gente[i];
        const e = persona.route.exitIndex;
        persona.tExit = Math.max(persona.tArr, libreDesde[e] ?? 0);
        libreDesde[e] = persona.tExit + 1 / cfg.caudalSalida;
    }
    const duracion = gente.reduce((m, p) => Math.max(m, p.tExit), 0);

    // Línea de tiempo por zonas (chips) y cola máxima.
    const frames: EvacuationFrame[] = [];
    let colaMaxima = 0;
    for (let t = 0; t <= Math.min(MAX_SECONDS, duracion) + DT; t += DT) {
        const counts: { [zoneId: string]: number } = {};
        zonas.forEach((z) => {
            counts[z.zone.id] = 0;
        });
        if (transitoId) counts[transitoId] = counts[transitoId] ?? 0;
        let remaining = 0;
        let cola = 0;
        gente.forEach((p) => {
            if (p.tExit > t) remaining += 1;
            if (t < p.tDep) {
                counts[p.zoneId] = (counts[p.zoneId] ?? 0) + 1;
            } else if (t < p.tExit) {
                if (transitoId) counts[transitoId] += 1;
                if (t >= p.tArr) cola += 1;
            }
        });
        colaMaxima = Math.max(colaMaxima, cola);
        frames.push({ t, counts, remaining });
    }

    const candidatas = resultados.filter((z) => z.zone.id !== transitoId);
    const ultimaZona =
        candidatas.length > 0
            ? candidatas.reduce((m, z) => (z.vaciadaEn > m.vaciadaEn ? z : m))
            : null;

    return {
        areaId,
        personas: total,
        duracion,
        objetivo: cfg.objetivo,
        cumple: duracion <= cfg.objetivo,
        ultimaZona,
        colaMaxima,
        zonas: resultados,
        transitoId,
        frames,
        gente,
    };
};
